import { useQuery } from '@apollo/client'
import Container from 'components/Container'
import OrderDetail from 'components/OrderDetail'
import withAuth from 'components/hocs/withAuth'
import { navigate } from 'gatsby'
import { GET_ORDERS } from 'lib/graphql/order'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { FiArrowLeft, FiLogOut } from 'react-icons/fi'
import { useDispatch, useSelector } from 'react-redux'
import { authStateSelector } from 'store/auth/selector'
import { authActions } from 'store/auth/slice'
import theme from 'styles/theme'
import { getAccessToken } from 'utils/auth'

function MyAccount() {
  const dispatch = useDispatch()
  const [orderId, setOrderId] = useState(null)
  const { data, loading } = useQuery(GET_ORDERS, {
    variables: {
      customerAccessToken: getAccessToken(),
    },
  })

  const authState = useSelector(authStateSelector)
  const { firstName, lastName, defaultAddress } = authState?.currentUser || {}
  const { orders } = data?.customer || {}
  const mappedOrders = (Object.values(orders?.edges || {}) || [])
    .sort((a, b) => new Date(b.node.processedAt) - new Date(a.node.processedAt))
    .map(({ node }) => node)

  return (
    <Container css={{ position: 'relative' }}>
      <Helmet title='My Account'>
        <meta name='robots' content='noindex' />
      </Helmet>
      <div
        css={{
          background: 'white',
          minHeight: '80vh',
          margin: '2.5rem',
          [theme.mediumDown]: {
            margin: 0,
            marginTop: '1rem',
          },
        }}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '2rem 4rem',
            borderBottom: '1px solid rgba(0, 0, 0, .2)',
            [theme.mediumDown]: {
              flexDirection: 'column',
              padding: '2rem 1rem',
            },
          }}>
          <h2
            css={{
              fontSize: '3rem',
              [theme.mediumDown]: {
                fontSize: '2rem',
              },
            }}>
            My Account
          </h2>
          {orderId ? (
            <button
              css={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 10,
                background: '#E5F1EC',
                borderRadius: 4,
                gap: 6,
                border: 'none',
                outline: 'none',
                cursor: 'pointer',
                marginTop: '1rem',
              }}
              onClick={() => setOrderId(null)}>
              <FiArrowLeft /> Return to Account Details
            </button>
          ) : (
            <button
              css={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 10,
                background: '#E5F1EC',
                borderRadius: 4,
                gap: 6,
                border: 'none',
                outline: 'none',
                cursor: 'pointer',
                marginTop: '1rem',
              }}
              onClick={() => dispatch(authActions.logout())}>
              <FiLogOut /> Log out
            </button>
          )}
        </div>
        <div
          css={{
            padding: '2rem 4rem',
            [theme.mediumDown]: {
              padding: '2rem 1rem',
            },
          }}>
          {orderId ? (
            <OrderDetail orderId={orderId} backToAccountPage={() => setOrderId(null)} />
          ) : (
            <>
              <h3 css={{ fontSize: '1.5rem' }}>Order History</h3>
              <div
                css={{
                  display: 'grid',
                  marginTop: '2rem',
                  gap: '3rem',
                  gridTemplateColumns: '2fr .75fr',
                  overflowX: 'auto',
                  [theme.mediumDown]: {
                    gridTemplateColumns: '1fr',
                  },
                }}>
                <table
                  css={{
                    borderRadius: '4px 4px 0 0',
                    borderCollapse: 'collapse',
                    border: '1px solid #D0DCDA',
                    height: 'min-content',
                    textAlign: 'left',
                    overflow: 'auto',
                  }}>
                  <thead
                    css={{
                      background: '#134F46',
                      color: 'white',
                      [theme.mediumDown]: {
                        border: 'none',
                        clip: 'rect(0 0 0 0)',
                        height: '1px',
                        margin: '-1px',
                        overflow: 'hidden',
                        padding: 0,
                        width: '1px',
                      },
                    }}>
                    <tr
                      css={{
                        [theme.mediumDown]: {
                          display: 'none',
                        },
                      }}>
                      <th
                        css={{
                          padding: '.75rem 1.25rem',
                        }}>
                        Order
                      </th>
                      <th
                        css={{
                          padding: '.75rem 1.25rem',
                        }}>
                        Date
                      </th>
                      <th
                        css={{
                          padding: '.75rem 1.25rem',
                        }}>
                        Payment Status
                      </th>
                      <th
                        css={{
                          padding: '.75rem 1.25rem',
                        }}>
                        Fulfillment Status
                      </th>
                      <th
                        css={{
                          padding: '.75rem 1.25rem',
                        }}>
                        TOTAL
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading &&
                      mappedOrders?.map(order => (
                        <tr
                          key={order.id}
                          css={{
                            cursor: 'pointer',
                            '&:hover': {
                              background: '#e1ede8',
                            },
                            [theme.mediumDown]: {
                              border: '1px solid #D0DCDA',
                            },
                          }}
                          onClick={() => setOrderId(order.id)}>
                          <td
                            data-label='Order'
                            css={{
                              padding: '1rem 1.25rem',
                              [theme.mediumDown]: {
                                display: 'block',
                                textAlign: 'right',
                                ':before': {
                                  content: 'attr(data-label)',
                                  float: 'left',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                },
                              },
                            }}>
                            <span
                              css={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                background: '#E5F1EC',
                                color: '#134F46',
                                padding: 6,
                                borderRadius: 4,
                                [theme.mediumDown]: {
                                  maxWidth: 'max-content',
                                  display: 'inline-block',
                                },
                              }}>
                              {order.orderNumber}
                            </span>
                          </td>
                          <td
                            data-label='Date'
                            css={{
                              padding: '1rem 1.25rem',
                              [theme.mediumDown]: {
                                display: 'block',
                                textAlign: 'right',
                                ':before': {
                                  content: 'attr(data-label)',
                                  float: 'left',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                },
                              },
                            }}>
                            {new Date(order.processedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </td>
                          <td
                            data-label='Payment Status'
                            css={{
                              padding: '1rem 1.25rem',
                              [theme.mediumDown]: {
                                display: 'block',
                                textAlign: 'right',
                                ':before': {
                                  content: 'attr(data-label)',
                                  float: 'left',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                },
                              },
                            }}>
                            {order.financialStatus}
                          </td>
                          <td
                            data-label='Fulfillment Status'
                            css={{
                              padding: '1rem 1.25rem',
                              [theme.mediumDown]: {
                                display: 'block',
                                textAlign: 'right',
                                ':before': {
                                  content: 'attr(data-label)',
                                  float: 'left',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                },
                              },
                            }}>
                            {order.fulfillmentStatus}
                          </td>
                          <td
                            data-label='TOTAL'
                            css={{
                              padding: '1rem 1.25rem',
                              [theme.mediumDown]: {
                                display: 'block',
                                textAlign: 'right',
                                ':before': {
                                  content: 'attr(data-label)',
                                  float: 'left',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                },
                              },
                            }}>
                            ${order.totalPrice.amount || order.totalPrice}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                <div
                  css={{
                    [theme.mediumDown]: {
                      padding: '1rem',
                    },
                  }}>
                  <h3 css={{ fontSize: '1.5rem' }}>Account Details</h3>
                  <div
                    css={{
                      margin: '1rem 0',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    <span
                      css={{
                        fontSize: '1rem',
                        marginBottom: '.5rem',
                      }}>
                      {firstName} {lastName}
                    </span>
                    <span
                      css={{
                        fontSize: '1rem',
                        marginBottom: '.5rem',
                      }}>
                      {defaultAddress?.address1}
                    </span>
                    <span
                      css={{
                        fontSize: '1rem',
                        marginBottom: '.5rem',
                      }}>
                      {defaultAddress?.city} {defaultAddress?.provinceCode} {defaultAddress?.zip}
                    </span>
                    <span
                      css={{
                        fontSize: '1rem',
                        marginBottom: '.5rem',
                      }}>
                      {defaultAddress?.country}
                    </span>
                  </div>
                  <button
                    css={{
                      marginTop: '1rem',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: 10,
                      background: '#E5F1EC',
                      borderRadius: 4,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate('/my-account/update')}>
                    Edit
                  </button>
                  <div css={{ marginTop: '1.5rem' }}>
                    <a
                      href='https://account.liveowyn.com/tools/recurring/login'
                      css={{ textDecoration: 'underline' }}>
                      Manage Subscriptions
                    </a>
                  </div>
                  <div css={{ marginTop: '1rem' }}>
                    <a
                      href='https://account.liveowyn.com/pages/rewards'
                      css={{ textDecoration: 'underline' }}>
                      View My Rewards Points
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Container>
  )
}
export default withAuth(MyAccount)
